import { savePuzzleProgress } from "../../../utils/persistenceUtils";
import { PuzzleNames, STATS_DIALOG_DELAY, TruthsortingData, TruthsortingStatement } from "../../../utils/types";
import { TruthsortingStore } from "./TruthsortingStore";

// Color to shape mapping for colorblind mode
export enum ShapeEnum {
  Circle = 'circle',
  Square = 'square',
  Triangle = 'triangle',
  Diamond = 'diamond',
  Heart = 'heart',
  Pentagon = 'pentagon',
  Trapezoid = 'trapezoid',
  Star = 'star'
}

export const colorToShape = (color: string): ShapeEnum => {
  switch (color.toLowerCase()) {
    case 'red':
      return ShapeEnum.Circle;
    case 'blue':
      return ShapeEnum.Square;
    case 'green':
      return ShapeEnum.Triangle;
    case 'yellow':
      return ShapeEnum.Diamond;
    case 'pink':
      return ShapeEnum.Heart;
    case 'orange':
      return ShapeEnum.Pentagon;
    case 'purple':
      return ShapeEnum.Trapezoid;
    case 'cyan':
      return ShapeEnum.Star;
    default:
      throw new Error(`Unknown color: ${color}`);
  }
};

export const initTruthsorting = (puzzleData: TruthsortingData) => {
  // Map solution indices to statement colors from the initial statements
  const solutionColors = puzzleData.solution.map(index => puzzleData.statements[index].color);

  TruthsortingStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.slots = puzzleData.slots;
    s.statements = puzzleData.statements;
    s.solution = puzzleData.solution;
    s.solutionColors = solutionColors;
  });
};

export const onReveal = () => {
  TruthsortingStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.Truthsorting, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
    });
  });

  setTimeout(() => {
    TruthsortingStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);
};

export const onSolve = () => {
  const { time, puzzleId } = TruthsortingStore.getRawState();

  TruthsortingStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.solved = true;
    s.isPaused = false;
  });

  setTimeout(() => {
    TruthsortingStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);

  savePuzzleProgress(PuzzleNames.Truthsorting, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
  });
};

export const saveTruthsorting = () => {
  const { solved, revealed, time, puzzleId } = TruthsortingStore.getRawState();

  if (solved || revealed) return;

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.Truthsorting, {
    puzzleId,
    revealed,
    solved,
    time,
  });
};

export const determineValidities = (slots: boolean[], statements: TruthsortingStatement[]): boolean[] => {
  return statements.map((statement, slotIdx) => {
    const shouldBeTrue = slots[slotIdx]; // white slot = true, black slot = false
    const isTrue = evaluateStatement(statement, statements, slotIdx, slots);
    // Valid if the statement's truth value matches what the slot requires
    return isTrue === shouldBeTrue;
  });
};

const evaluateStatement = (
  statement: TruthsortingStatement,
  allStatements: TruthsortingStatement[],
  myPos: number,
  slots: boolean[]
): boolean => {
  const req = statement.requirement;
  const p = req.params;

  // Helper to find position of a color in the current arrangement
  const findColorPos = (color: string): number => {
    return allStatements.findIndex(s => s.color === color);
  };

  switch (req.type) {
    case 'NthStatement':
      return myPos === p.position;
    case 'NegatedNthStatement':
      return myPos !== p.position;
    case 'FollowedByVeracity':
      if (myPos >= allStatements.length - 1) return false;
      // Check if the slot after matches the required veracity
      return slots[myPos + 1] === p.veracity;
    case 'PrecededByVeracity':
      if (myPos <= 0) return false;
      // Check if the slot before matches the required veracity
      return slots[myPos - 1] === p.veracity;
    case 'FollowedByColor': {
      const targetPos = findColorPos(p.color);
      return targetPos === myPos + 1;
    }
    case 'NegatedFollowedByColor': {
      const targetPos = findColorPos(p.color);
      return targetPos !== myPos + 1;
    }
    case 'PrecededByColor': {
      const targetPos = findColorPos(p.color);
      return targetPos === myPos - 1;
    }
    case 'NegatedPrecededByColor': {
      const targetPos = findColorPos(p.color);
      return targetPos !== myPos - 1;
    }
    case 'ColorVeracityAssertion': {
      const targetPos = findColorPos(p.color);
      const targetSlotIsTrue = slots[targetPos];
      return targetSlotIsTrue === p.veracity;
    }
    case 'PositionalParity': {
      // User sees position 1,2,3... we see 0,1,2...
      const userPosition = myPos + 1;
      const isUserEven = userPosition % 2 === 0;
      return isUserEven === p.isEven;
    }
    case 'SurroundedBy':
      if (myPos <= 0 || myPos >= allStatements.length - 1) return false;
      // Check if BOTH adjacent slots match the required veracity
      const leftSlotIsTrue = slots[myPos - 1];
      const rightSlotIsTrue = slots[myPos + 1];
      return leftSlotIsTrue === p.veracity && rightSlotIsTrue === p.veracity;
    case 'NegatedSurroundedBy':
      if (myPos <= 0 || myPos >= allStatements.length - 1) return true;
      // Check if NOT both adjacent slots match the required veracity
      const leftTrue = slots[myPos - 1];
      const rightTrue = slots[myPos + 1];
      return !(leftTrue === p.veracity && rightTrue === p.veracity);
    case 'ColorPosition': {
      const targetPos = findColorPos(p.color);
      return targetPos === p.position;
    }
    case 'NegatedColorPosition': {
      const targetPos = findColorPos(p.color);
      return targetPos !== p.position;
    }
    case 'DistanceFromColor': {
      const targetPos = findColorPos(p.color);
      return Math.abs(targetPos - myPos) - 1 === p.gap;
    }
    case 'ColorOrder': {
      const pos1 = findColorPos(p.colorOne);
      const pos2 = findColorPos(p.colorTwo);
      return pos1 < pos2;
    }
    case 'BetweenColors': {
      const pos1 = findColorPos(p.colorOne);
      const pos2 = findColorPos(p.colorTwo);
      const minPos = Math.min(pos1, pos2);
      const maxPos = Math.max(pos1, pos2);
      // "Directly between" means the two colors are exactly 2 positions apart
      // and this statement is in the middle position
      return maxPos - minPos === 2 && myPos === minPos + 1;
    }
    case 'BothVeracityAssertion': {
      const firstPos = findColorPos(p.firstStatement);
      const secondPos = findColorPos(p.secondStatement);
      const firstSlotIsTrue = slots[firstPos];
      const secondSlotIsTrue = slots[secondPos];
      return firstSlotIsTrue === p.veracity && secondSlotIsTrue === p.veracity;
    }
    case 'XORVeracityAssertion': {
      const firstPos = findColorPos(p.firstStatement);
      const secondPos = findColorPos(p.secondStatement);
      const firstSlotIsTrue = slots[firstPos];
      const secondSlotIsTrue = slots[secondPos];
      const firstMatches = firstSlotIsTrue === p.veracity;
      const secondMatches = secondSlotIsTrue === p.veracity;
      // XOR: exactly one must match, not both and not neither
      return firstMatches !== secondMatches;
    }
    case 'SelfIsCloserToColor': {
      const pos1 = findColorPos(p.colorOne);
      const pos2 = findColorPos(p.colorTwo);
      const dist1 = Math.abs(myPos - pos1);
      const dist2 = Math.abs(myPos - pos2);
      return dist1 < dist2;
    }
    case 'SelfIsFirstOrLast':
      return myPos === 0 || myPos === allStatements.length - 1;
    case 'NegatedSelfIsFirstOrLast':
      return myPos !== 0 && myPos !== allStatements.length - 1;
    case 'ColorIsFirstOrLast': {
      const targetPos = findColorPos(p.colorOne);
      return targetPos === 0 || targetPos === allStatements.length - 1;
    }
    case 'NegatedColorIsFirstOrLast': {
      const targetPos = findColorPos(p.colorOne);
      return targetPos !== 0 && targetPos !== allStatements.length - 1;
    }
    case 'SelfAdjacency': {
      const targetPos = findColorPos(p.colorOne);
      return Math.abs(targetPos - myPos) === 1;
    }
    case 'NegatedSelfAdjacency': {
      const targetPos = findColorPos(p.colorOne);
      return Math.abs(targetPos - myPos) !== 1;
    }
    case 'ColorAdjacency': {
      const pos1 = findColorPos(p.colorOne);
      const pos2 = findColorPos(p.colorTwo);
      return Math.abs(pos1 - pos2) === 1;
    }
    case 'XNORVeracityAssertion': {
      const pos1 = findColorPos(p.colorOne);
      const pos2 = findColorPos(p.colorTwo);
      const slot1IsTrue = slots[pos1];
      const slot2IsTrue = slots[pos2];
      // XNOR: both are the same (both true or both false)
      return slot1IsTrue === slot2IsTrue;
    }
    default:
      throw new Error(`Unknown requirement type: ${req.type}`);
  }
};